import React, { FC, ReactElement, useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  Stack,
  Typography,
  Tooltip,
  IconButton,
  CircularProgress,
  Checkbox,
} from "@mui/material";
import EditIcon from "@/assets/svgs/edit.svg";
import DeleteIcon from "@/assets/svgs/garbage.svg";
import InfoIcon from "@/assets/svgs/info.svg";
import FactoryIcon from "@mui/icons-material/Factory";
import FlagIcon from "@mui/icons-material/Flag";
import HighRiskIcon from "@/assets/svgs/HighRisk.svg";
import MediumRiskIcon from "@/assets/svgs/MediumRisk.svg";
import LowRiskIcon from "@/assets/svgs/LowRisk.svg";
import HelpIcon from "@mui/icons-material/Help";
import { DynamicTable, RiskAnalysisTooltip, HTSCodeWithTooltip } from "@/components";
import Pagination from "@/components/paginations/pagination";
import { tableCompactSx } from "@/components/table/table-styles";
import { ProductWithTariffs, LineType } from "@/services/product-catalogue";
import { getCountryFlag } from "@/constants/country-flags";
import TariffBreakdownPopover from "../tariff-breakdown-popover";
import { tokens } from "@/tokens";

interface ProductTableProps {
  data: ProductWithTariffs[];
  currentPage: number;
  pageSize: number;
  isLoading: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  totalCount: number;
  onEditProduct: (product: ProductWithTariffs) => void;
  onDeleteProduct: (productId: string) => void;
  isProductPending?: (productId: string) => boolean;
  selectedIds?: Set<string>;
  onSelectionChange?: (ids: Set<string>) => void;
}

const toLowerSafe = (value: unknown): string => {
  if (typeof value === "string") return value.toLowerCase();
  if (value === null || value === undefined) return "";
  return String(value).toLowerCase();
};

// Reusable component for truncated text with hover tooltip
const TruncatedTextCell: React.FC<{ text: string }> = ({ text }) => {
  const textRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const displayText = text || "-";

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        const isOverflowing = textRef.current.scrollWidth > textRef.current.clientWidth;
        setIsTruncated(isOverflowing);
      }
    };

    checkTruncation();
    // Recheck on window resize
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [displayText]);

  const tooltipProps = {
    title: displayText,
    arrow: true,
    placement: "top" as const,
    slotProps: {
      popper: {
        modifiers: [
          {
            name: 'offset',
            options: { offset: [0, -8] },
          },
        ],
      },
      tooltip: {
        sx: {
          bgcolor: tokens.text.primary,
          color: 'white',
          fontSize: tokens.fontSize.lg,
          borderRadius: tokens.radius.md,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
        },
      },
    },
  };

  const cellSx = { 
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: tokens.fontSize.base,
    fontWeight: tokens.fontWeight.normal,
    lineHeight: '20px',
    color: tokens.text.primary
  };

  const content = (
    <Box ref={textRef} sx={cellSx}>
      {displayText}
    </Box>
  );

  if (isTruncated) {
    return (
      <Tooltip {...tooltipProps}>
        {content}
      </Tooltip>
    );
  }

  return content;
};

const DescriptionCell: React.FC<{ text: string }> = ({ text }) => {
  return <TruncatedTextCell text={text} />;
};

const ProductTable: FC<ProductTableProps> = ({
  data,
  currentPage,
  pageSize,
  isLoading,
  onPageChange,
  onPageSizeChange,
  totalCount,
  onEditProduct,
  onDeleteProduct,
  isProductPending,
  selectedIds,
  onSelectionChange,
}): ReactElement => {
  // State for breakdown drawer
  const [selectedProduct, setSelectedProduct] = useState<ProductWithTariffs | null>(null);

  const showCheckboxes = !!selectedIds && !!onSelectionChange;

  const handleToggleRow = useCallback((id: string) => {
    if (!selectedIds || !onSelectionChange) return;
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    onSelectionChange(next);
  }, [selectedIds, onSelectionChange]);

  const handleToggleAll = useCallback(() => {
    if (!selectedIds || !onSelectionChange) return;
    const allOnPage = data.map(p => p._id);
    const allSelected = allOnPage.length > 0 && allOnPage.every(id => selectedIds.has(id));
    if (allSelected) {
      // Deselect all on current page
      const next = new Set(selectedIds);
      allOnPage.forEach(id => next.delete(id));
      onSelectionChange(next);
    } else {
      // Select all on current page
      const next = new Set(selectedIds);
      allOnPage.forEach(id => next.add(id));
      onSelectionChange(next);
    }
  }, [data, selectedIds, onSelectionChange]);
  
  const handleOpenBreakdown = (_event: React.MouseEvent<HTMLElement>, product: ProductWithTariffs) => {
    setSelectedProduct(product);
  };

  const handleCloseBreakdown = () => {
    setSelectedProduct(null);
  };

  const allOnPageSelected = showCheckboxes && data.length > 0 && data.every(p => selectedIds!.has(p._id));
  const someOnPageSelected = showCheckboxes && data.some(p => selectedIds!.has(p._id));

  // Build dynamic columns including tariff columns
  const columns = [
    ...(showCheckboxes ? [{
      key: "checkbox" as const,
      name: "",
      align: "center" as const,
      width: "4%",
      headerComponent: () => (
        <Checkbox
          size="small"
          checked={allOnPageSelected}
          indeterminate={someOnPageSelected && !allOnPageSelected}
          onChange={handleToggleAll}
          sx={{ p: 0, color: tokens.text.muted, '&.Mui-checked': { color: tokens.brand.primary }, '&.MuiCheckbox-indeterminate': { color: tokens.brand.primary } }}
        />
      ),
      component: (row: ProductWithTariffs) => (
        <Checkbox
          size="small"
          checked={selectedIds!.has(row._id)}
          onChange={() => handleToggleRow(row._id)}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          sx={{ p: 0, color: tokens.text.muted, '&.Mui-checked': { color: tokens.brand.primary } }}
        />
      ),
    }] : []),
    {
      key: "sku" as keyof ProductWithTariffs,
      name: "SKU",
      align: "left" as const,
      width: showCheckboxes ? "12%" : "13%",
      sorting: true,
      component: (row: ProductWithTariffs) => (
        <TruncatedTextCell text={row.sku} />
      ),
    },
    {
      key: "name" as keyof ProductWithTariffs,
      name: "Product Name",
      align: "left" as const,
      width: "16%",
      sorting: true,
      component: (row: ProductWithTariffs) => (
        <TruncatedTextCell text={row.name} />
      ),
    },
    {
      key: "description" as keyof ProductWithTariffs,
      name: "Description",
      align: "left" as const,
      width: "20%",
      component: (row: ProductWithTariffs) => (
        <DescriptionCell text={row.description || "-"} />
      ),
    },
    {
      key: "htsCode" as keyof ProductWithTariffs,
      name: "HTS Code",
      align: "left" as const,
      width: "14%",
      sorting: true,
      component: (row: ProductWithTariffs) => {
        const hasGeneralDuty = row.breakdownLines?.some(line => 
          line.calculationBreakdown?.some(calc => 
            calc.applied && (
              toLowerSafe(calc.columnName).includes('general duty') ||
              toLowerSafe(calc.columnName).includes('hts') ||
              calc.columnId === 'general-duty'
            )
          )
        );
        return (
          <HTSCodeWithTooltip htsCode={row.htsCode}>
            <Typography
              sx={{
                color: !hasGeneralDuty ? tokens.status.danger : tokens.text.primary,
                fontWeight: !hasGeneralDuty ? tokens.fontWeight.bold : tokens.fontWeight.normal,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontSize: tokens.fontSize.base,
                lineHeight: '20px',
                cursor: 'pointer',
                '&:hover': {
                  color: tokens.brand.primary,
                  textDecoration: 'underline',
                },
              }}
            >
              {row.htsCode}
            </Typography>
          </HTSCodeWithTooltip>
        );
      },
    },
    {
      key: "countryOfOrigin" as keyof ProductWithTariffs,
      name: "Country",
      align: "left" as const,
      width: "10%",
      sorting: true,
      component: (row: ProductWithTariffs) => (
        <Stack direction="row" alignItems="center" spacing={1}>
          <span>{getCountryFlag(row.countryOfOrigin)}</span>
          <Typography sx={{ fontSize: tokens.fontSize.base, fontWeight: tokens.fontWeight.normal, lineHeight: '20px', color: tokens.text.primary }}>
            {row.countryOfOrigin}
          </Typography>
        </Stack>
      ),
    },
    {
      key: "riskAnalysis" as keyof ProductWithTariffs,
      name: "AI Risk Score",
      align: "left" as const,
      width: "10%",
      component: (row: ProductWithTariffs) => {
        // Check if this product is pending analysis
        const isPending = isProductPending && isProductPending(row._id);
        
        if (isPending) {
          return (
            <RiskAnalysisTooltip riskAnalysis={{ riskLevel: 'PENDING', riskReason: 'Analysis pending...', analyzedAt: new Date().toISOString(), analysisVersion: '1.0' }}>
              <Box sx={{ 
                backgroundColor: tokens.bg.surface,
                border: `1px solid ${tokens.border.default}`,
                borderRadius: tokens.radius.sm,
                padding: '4px 8px 4px 4px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <CircularProgress size={12} sx={{ color: tokens.text.secondary }} />
                <Typography sx={{ fontSize: tokens.fontSize.base, fontWeight: tokens.fontWeight.semibold, color: tokens.text.secondary }}>
                  Pending
                </Typography>
              </Box>
            </RiskAnalysisTooltip>
          );
        }
        
        if (!row.riskAnalysis) {
          return (
            <Typography sx={{ fontSize: tokens.fontSize.base, fontWeight: tokens.fontWeight.normal, lineHeight: '20px', color: tokens.text.primary }}>
              N/A
            </Typography>
          );
        }
        
        const level = toLowerSafe((row.riskAnalysis as any).riskLevel);
        // Map risk level to display text (capitalize)
        const displayText = level ? (level.charAt(0).toUpperCase() + level.slice(1)) : "N/A";
        
        let iconSrc;
        if (level === 'high') {
            iconSrc = HighRiskIcon;
        } else if (level === 'medium') {
            iconSrc = MediumRiskIcon;
        } else if (level === 'low') {
            iconSrc = LowRiskIcon;
        }

        return (
          <RiskAnalysisTooltip riskAnalysis={row.riskAnalysis}>
            <Box sx={{ 
              backgroundColor: tokens.bg.surface,
              border: `1px solid ${tokens.border.default}`,
              borderRadius: tokens.radius.sm,
              padding: '4px 8px 4px 4px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              {iconSrc ? (
                <img src={iconSrc} alt={`${displayText} risk`} style={{ width: 16, height: 16 }} />
              ) : (
                <HelpIcon sx={{ fontSize: 16, color: tokens.text.secondary }} />
              )}
              <Typography sx={{ fontSize: tokens.fontSize.base, fontWeight: tokens.fontWeight.semibold, color: tokens.text.secondary }}>
                {displayText}
              </Typography>
            </Box>
          </RiskAnalysisTooltip>
        );
      },
    },
    // Total column with breakdown
    {
      key: "total-tariffs" as keyof ProductWithTariffs,
      name: "Total Duty",
      align: "left" as const,
      width: "15%",
      component: (row: ProductWithTariffs) => {
        const totalRate = (row.totalDutyRate ?? row.additionalTariffs.reduce((sum, tariff) => sum + tariff.rate, 0));
        // Check if there's a metal/non-metal split in the breakdown
        const hasMetalSplit = row.breakdownLines && row.breakdownLines.length > 0 && 
          row.breakdownLines.some(line => line.lineType === LineType.METAL || line.lineType === LineType.NONMETAL || line.lineType === LineType.STEEL || line.lineType === LineType.ALUMINUM || line.lineType === LineType.COPPER);
        const activeDutyCount = row.additionalTariffs.filter(t => t.rate > 0).length;
        
        // Check if HTS General Duty is missing
        const hasGeneralDuty = row.breakdownLines?.some(line => 
          line.calculationBreakdown?.some(calc => 
            calc.applied && (
              toLowerSafe(calc.columnName).includes('general duty') ||
              toLowerSafe(calc.columnName).includes('hts') ||
              calc.columnId === 'general-duty'
            )
          )
        );
        
        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography sx={{ fontSize: tokens.fontSize.base, fontWeight: tokens.fontWeight.normal, lineHeight: '20px', color: tokens.text.primary }}>
              {(totalRate * 100).toFixed(2)}%
            </Typography>
            
            {/* Info Icon Trigger */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {row.additionalTariffs.length > 0 && (
                  <Tooltip 
                    title={`View ${activeDutyCount} duty type${activeDutyCount !== 1 ? 's' : ''} breakdown`}
                    placement="top"
                  >
                    <IconButton
                      size="small"
                      onClick={(e) => handleOpenBreakdown(e, row)}
                      sx={{ 
                        p: 0, 
                        color: tokens.text.muted,
                        width: 20,
                        height: 20,
                      }}
                    >
                      <img src={InfoIcon} alt="Info" style={{ width: 16, height: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
                
                {!hasGeneralDuty && (
                  <Tooltip title="HTS code not found in database" placement="top">
                    <FlagIcon sx={{ fontSize: 16, color: tokens.status.danger }} />
                  </Tooltip>
                )}
                {hasMetalSplit && (
                  <Tooltip title="Duty calculation split by metal/non-metal content" placement="top">
                    <FactoryIcon sx={{ fontSize: 16, color: tokens.status.warning }} />
                  </Tooltip>
                )}
            </Box>
          </Stack>
        );
      },
    },
    {
      key: "actions" as const,
      name: "",
      align: "right" as const,
      width: "6%",
      component: (row: ProductWithTariffs) => (
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Tooltip title="Edit Product">
            <IconButton size="small" onClick={() => onEditProduct(row)} sx={{ color: tokens.text.primary }}>
              <img src={EditIcon} alt="Edit" style={{ width: 20, height: 20 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Product">
            <IconButton size="small" onClick={() => onDeleteProduct(row._id)} sx={{ color: tokens.text.primary }}>
              <img src={DeleteIcon} alt="Delete" style={{ width: 20, height: 20 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <Stack spacing={0} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Table */}
      <DynamicTable
        data={data}
        columns={columns}
        isLoading={isLoading}
        limit={pageSize}
        page={currentPage}
        total={totalCount}
        handlePageChange={onPageChange}
        handleLimitChange={onPageSizeChange}
        stickyHeader={true}
        hideScrollBars={false}
        isFullHeight={true}
        hidePagination={true}
        square={false}
        borderLess={true}
        sx={{
          '& .MuiTableContainer-root': {
            '&::-webkit-scrollbar': { height: 6, width: 6 },
            backgroundColor: tokens.bg.surface,
            border: 'none',
          },
          '& .MuiTable-root': {
            tableLayout: 'fixed',
            width: '100%',
            minWidth: { xs: 800, md: '100%' },
            border: 'none',
            borderCollapse: 'separate',
            borderSpacing: 0,
            height: 'auto',
            ...tableCompactSx,
          },
        }}
      />
      <Pagination
        page={currentPage}
        limit={pageSize}
        total={totalCount}
        onPageChange={onPageChange}
        onLimitChange={onPageSizeChange}
      />
      
      {/* Tariff Breakdown Drawer */}
      <TariffBreakdownPopover
        open={Boolean(selectedProduct)}
        onClose={handleCloseBreakdown}
        productName={selectedProduct?.name}
        productSku={selectedProduct?.sku}
        htsCode={selectedProduct?.htsCode}
        countryOfOrigin={selectedProduct?.countryOfOrigin}
        breakdownLines={selectedProduct?.breakdownLines}
        additionalTariffs={selectedProduct?.additionalTariffs}
        totalRate={selectedProduct ? (selectedProduct.totalDutyRate ?? selectedProduct.additionalTariffs.reduce((sum, t) => sum + t.rate, 0)) : 0}
        metalPercentage={selectedProduct?.metalPercentage}
        ftaApplicable={selectedProduct?.ftaApplicable}
        ftaProgramName={selectedProduct?.ftaProgramName}
        secondaryDutyRateString={(selectedProduct as any)?.secondaryDutyRateString}
      />
    </Stack>
  );
};

export default React.memo(ProductTable, (prevProps, nextProps) => {
  // Custom comparison function for optimization
  // Only re-render if data actually changed
  return (
    prevProps.data === nextProps.data &&
    prevProps.currentPage === nextProps.currentPage &&
    prevProps.pageSize === nextProps.pageSize &&
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.totalCount === nextProps.totalCount &&
    prevProps.selectedIds === nextProps.selectedIds
  );
});
